/* ============================================
   AI Chat Widget - Production-Ready CSS
   Modern, Professional, Accessible Design
   ============================================ */

/* ============================================
   CSS CUSTOM PROPERTIES (Design Tokens)
   ============================================ */
:root {
  /* === Primary Design Tokens === */
  --primary-color: #3b82f6;
  --primary-color-hover: #2563eb;
  --primary-color-light: rgba(59, 130, 246, 0.1);
  --secondary-color: #1e293b;

  /* === Typography === */
  --font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  --font-size-xs: 0.6875rem; /* 11px */
  --font-size-sm: 0.8125rem; /* 13px */
  --font-size-base: 0.875rem; /* 14px */
  --font-size-md: 0.9375rem; /* 15px */
  --font-size-lg: 1rem; /* 16px */
  --font-size-xl: 1.125rem; /* 18px */
  --font-weight-normal: 400;
  --font-weight-medium: 500;
  --font-weight-semibold: 600;
  --line-height-tight: 1.3;
  --line-height-normal: 1.5;
  --line-height-relaxed: 1.625;

  /* === Text Colors === */
  --text-primary: #0f172a;
  --text-secondary: #475569;
  --text-muted: #64748b;
  --text-light: #ffffff;
  --text-dark: #1e293b;
  --input-text: #1e293b;

  /* === Background Colors === */
  --bg-primary: #ffffff;
  --bg-secondary: #f8fafc;
  --bg-tertiary: #f1f5f9;
  --bg-light: #ffffff;
  --bg-gray: #f8fafc;
  --bg-input: #f1f5f9;
  --bg-input-focus: #ffffff;
  --bg-hover: #e2e8f0;

  /* === Border & Dividers === */
  --border-color: #e2e8f0;
  --border-light: #e2e8f0;
  --border-focus: var(--primary-color);
  --border-radius-sm: 8px;
  --border-radius-md: 12px;
  --border-radius-lg: 16px;
  --border-radius-xl: 20px;
  --border-radius-full: 9999px;

  /* === Shadows (Layered for depth) === */
  --shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.04);
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.05);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
  --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  --shadow-color: rgba(0, 0, 0, 0.08);
  --shadow-primary: rgba(59, 130, 246, 0.25);
  --shadow-primary-lg: 0 10px 40px -10px rgba(59, 130, 246, 0.4);

  /* === Overlays === */
  --overlay-light: rgba(255, 255, 255, 0.1);
  --overlay-medium: rgba(255, 255, 255, 0.18);
  --overlay-dark: rgba(255, 255, 255, 0.25);
  --backdrop-blur: blur(8px);

  /* === Code Styling === */
  --code-bg: #f1f5f9;
  --code-text: #0f172a;
  --code-bg-dark: rgba(255, 255, 255, 0.12);

  /* === State Colors === */
  --success-color: #22c55e;
  --success-bg: #dcfce7;
  --warning-color: #f59e0b;
  --warning-bg: #fef3c7;
  --error-color: #ef4444;
  --error-bg: #fee2e2;
  --offline-color: #fef2f2;
  --offline-text: #991b1b;
  --offline-border: #fecaca;

  /* === Animation === */
  --typing-dot: #94a3b8;
  --transition-fast: 150ms;
  --transition-normal: 200ms;
  --transition-slow: 300ms;
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
  --ease-in-out: cubic-bezier(0.4, 0, 0.2, 1);
  --ease-spring: cubic-bezier(0.34, 1.56, 0.64, 1);

  /* === Spacing Scale === */
  --space-1: 4px;
  --space-2: 8px;
  --space-3: 12px;
  --space-4: 16px;
  --space-5: 20px;
  --space-6: 24px;
  --space-8: 32px;
  --space-10: 40px;

  /* === Z-Index Scale === */
  --z-dropdown: 100;
  --z-sticky: 200;
  --z-overlay: 500;
  --z-modal: 1000;
  --z-popover: 1100;
  --z-tooltip: 1200;
  --z-widget: 10000;
  --z-widget-button: 10001;
}

/* ============================================
   DARK THEME OVERRIDES
   ============================================ */
.theme-dark {
  /* Dark theme design tokens */
  --primary-color: #60a5fa;
  --primary-color-hover: #3b82f6;
  --primary-color-light: rgba(96, 165, 250, 0.15);
  --secondary-color: #f1f5f9;

  --text-primary: #f1f5f9;
  --text-secondary: #cbd5e1;
  --text-muted: #94a3b8;
  --text-light: #ffffff;
  --text-dark: #f1f5f9;
  --input-text: #f1f5f9;

  --bg-primary: #0f172a;
  --bg-secondary: #1e293b;
  --bg-tertiary: #334155;
  --bg-light: #1e293b;
  --bg-gray: #0f172a;
  --bg-input: #334155;
  --bg-input-focus: #475569;
  --bg-hover: #475569;

  --border-color: #334155;
  --border-light: #475569;

  --shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.2);
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.25), 0 1px 2px rgba(0, 0, 0, 0.2);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.25);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.35), 0 4px 6px -4px rgba(0, 0, 0, 0.25);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 8px 10px -6px rgba(0, 0, 0, 0.3);
  --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  --shadow-color: rgba(0, 0, 0, 0.3);
  --shadow-primary: rgba(96, 165, 250, 0.3);
  --shadow-primary-lg: 0 10px 40px -10px rgba(96, 165, 250, 0.5);

  --overlay-light: rgba(0, 0, 0, 0.2);
  --overlay-medium: rgba(0, 0, 0, 0.35);
  --overlay-dark: rgba(0, 0, 0, 0.5);

  --code-bg: #334155;
  --code-text: #e2e8f0;
  --code-bg-dark: rgba(0, 0, 0, 0.25);

  --typing-dot: #64748b;

  --offline-color: #450a0a;
  --offline-text: #fca5a5;
  --offline-border: #7f1d1d;
}

/* Dark theme specific component overrides */
.theme-dark .chat-window {
  background-color: var(--bg-primary);
  border: 1px solid var(--border-color);
}

.theme-dark .chat-header {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-hover) 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.theme-dark .message.bot {
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.theme-dark .message.user {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-hover) 100%);
  color: var(--text-light);
}

.theme-dark .chat-input {
  background-color: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
}

.theme-dark .chat-input input {
  background-color: var(--bg-input);
  color: var(--input-text);
  border: 1px solid var(--border-color);
}

.theme-dark .chat-input input:focus {
  background-color: var(--bg-input-focus);
  border-color: var(--primary-color);
}

.theme-dark .chat-input input::placeholder {
  color: var(--text-muted);
}

.theme-dark .message code {
  background-color: var(--code-bg);
  color: var(--code-text);
  border: 1px solid var(--border-color);
}

.theme-dark .message.user code {
  background-color: var(--code-bg-dark);
  color: var(--text-light);
  border: none;
}

.theme-dark .typing-bubble {
  background-color: var(--typing-dot);
}

.theme-dark .chat-attribution {
  background-color: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
}

.theme-dark .chat-attribution a {
  color: var(--text-muted);
}

.theme-dark .chat-attribution a:hover {
  color: var(--primary-color);
}

/* ============================================
   GLOBAL TRANSITIONS (Smooth theme switching)
   ============================================ */
.ai-chat-widget-container,
.chat-window,
.chat-header,
.chat-messages,
.message,
.chat-input,
.chat-input input,
.chat-input button,
.message code,
.typing-bubble,
.chat-attribution {
  transition:
    background-color var(--transition-normal) var(--ease-in-out),
    color var(--transition-normal) var(--ease-in-out),
    border-color var(--transition-normal) var(--ease-in-out),
    box-shadow var(--transition-normal) var(--ease-in-out);
}

/* ============================================
   WIDGET CONTAINER
   ============================================ */
.ai-chat-widget-container {
  font-family: var(--font-family);
  font-size: var(--font-size-base);
  line-height: var(--line-height-normal);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ============================================
   FLOATING ACTION BUTTON (Toggle)
   ============================================ */
.ai-chat-widget-container .chat-toggle {
  position: fixed;
  width: 60px;
  height: 60px;
  border-radius: var(--border-radius-full);
  border: none;
  color: var(--text-light);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-hover) 100%);
  bottom: var(--space-5);
  right: var(--space-5);
  z-index: var(--z-widget-button);
  pointer-events: auto;
  box-shadow: var(--shadow-primary-lg), var(--shadow-lg);
  transition:
    transform var(--transition-normal) var(--ease-spring),
    box-shadow var(--transition-normal) var(--ease-out);
}

.ai-chat-widget-container .chat-toggle::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%);
  pointer-events: none;
}

.ai-chat-widget-container .chat-toggle svg {
  width: 26px;
  height: 26px;
  stroke-width: 2;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.ai-chat-widget-container.left .chat-toggle {
  left: var(--space-5);
  right: auto;
}

.chat-toggle:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: var(--shadow-primary-lg), var(--shadow-xl);
}

.chat-toggle:active {
  transform: translateY(-1px) scale(0.98);
}

.chat-toggle:focus-visible {
  outline: 3px solid var(--primary-color-light);
  outline-offset: 3px;
}

/* ============================================
   CHAT WINDOW
   ============================================ */
.chat-window {
  position: absolute;
  bottom: 80px;
  right: 0;
  width: 400px;
  height: min(640px, 80vh);
  max-height: 80vh;
  background: var(--bg-primary);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-2xl);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform-origin: bottom right;
  animation: slideIn var(--transition-slow) var(--ease-out);
  z-index: var(--z-widget);
  pointer-events: auto;
  border: 1px solid var(--border-color);
}

/* Position for left-aligned widget */
.ai-chat-widget-container.left .chat-window {
  right: auto;
  left: 0;
  transform-origin: bottom left;
}

/* Window open animation */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Window close animation */
.chat-window.closing {
  animation: slideOut var(--transition-normal) var(--ease-in-out) forwards;
}

@keyframes slideOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }
}

/* ============================================
   CHAT HEADER
   ============================================ */
.chat-header {
  padding: var(--space-4) var(--space-5);
  color: var(--text-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-hover) 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
  min-height: 64px;
  position: relative;
}

/* Subtle shine effect on header */
.chat-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, transparent 100%);
  pointer-events: none;
}

.header-title {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  position: relative;
  z-index: 1;
}

/* Bot Logo / Avatar */
.bot-logo {
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius-full);
  overflow: visible;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  position: relative;
  backdrop-filter: blur(4px);
}

/* Status indicator (online dot) */
.bot-logo::after {
  content: "";
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  background-color: var(--success-color);
  border: 2px solid rgba(255, 255, 255, 0.95);
  border-radius: var(--border-radius-full);
  z-index: 2;
  transform: translate(20%, 20%);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.bot-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--border-radius-full);
  display: block;
}

.chat-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.01em;
  color: var(--text-light);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Header Buttons Group */
.header-buttons-grp {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  position: relative;
  z-index: 1;
}

.chat-header button,
.header-buttons {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: var(--text-light);
  cursor: pointer;
  width: 34px;
  height: 34px;
  border-radius: var(--border-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color var(--transition-fast) var(--ease-out),
    transform var(--transition-fast) var(--ease-out);
  padding: 0;
}

.chat-header button:hover,
.header-buttons:hover {
  background-color: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.chat-header button:active,
.header-buttons:active {
  transform: scale(0.95);
}

.chat-header button:focus-visible,
.header-buttons:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.5);
  outline-offset: 2px;
}

.chat-header button svg,
.header-buttons svg {
  width: 18px;
  height: 18px;
}

/* ============================================
   CHAT MESSAGES CONTAINER
   ============================================ */
.chat-messages {
  flex: 1;
  padding: var(--space-5);
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  scroll-behavior: smooth;
  background-color: var(--bg-secondary);
  overscroll-behavior: contain;
}

/* Custom Scrollbar - Modern & Subtle */
.chat-messages::-webkit-scrollbar {
  width: 8px;
}

.chat-messages::-webkit-scrollbar-track {
  background: transparent;
  margin: var(--space-2) 0;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: var(--border-radius-full);
  border: 2px solid transparent;
  background-clip: padding-box;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
  border: 2px solid transparent;
  background-clip: padding-box;
}

/* Firefox scrollbar */
.chat-messages {
  scrollbar-width: thin;
  scrollbar-color: var(--border-color) transparent;
}

/* ============================================
   MESSAGE BUBBLES
   ============================================ */
.message {
  margin-bottom: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--border-radius-lg);
  max-width: 85%;
  line-height: var(--line-height-relaxed);
  font-size: var(--font-size-base);
  position: relative;
  color: var(--text-primary);
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

/* Bot Message Bubble */
.message.bot {
  background: var(--bg-primary);
  margin-right: auto;
  box-shadow: var(--shadow-sm);
  border-bottom-left-radius: var(--space-1);
  border: 1px solid var(--border-color);
}

/* User Message Bubble */
.message.user {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-hover) 100%);
  color: var(--text-light);
  margin-left: auto;
  border-bottom-right-radius: var(--space-1);
  box-shadow:
    var(--shadow-md),
    0 2px 8px var(--shadow-primary);
}

/* Message Typography */
.message p {
  margin: 0 0 var(--space-2) 0;
}

.message p:last-child {
  margin-bottom: 0;
}

.message h1,
.message h2,
.message h3,
.message h4 {
  margin-top: var(--space-3);
  margin-bottom: var(--space-2);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-tight);
}

.message h1:first-child,
.message h2:first-child,
.message h3:first-child,
.message h4:first-child {
  margin-top: 0;
}

.message h1 {
  font-size: 1.375em;
}
.message h2 {
  font-size: 1.25em;
}
.message h3 {
  font-size: 1.125em;
}
.message h4 {
  font-size: 1em;
}

/* Lists */
.message ul,
.message ol {
  margin: var(--space-2) 0;
  padding-left: 1.5em;
}

.message ol {
  list-style-type: decimal;
}
.message ul {
  list-style-type: disc;
}

.message li {
  margin-bottom: var(--space-1);
  line-height: var(--line-height-normal);
}

.message li:last-child {
  margin-bottom: 0;
}

/* Links */
.message a {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  transition: color var(--transition-fast) var(--ease-out);
}

.message a:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.message.user a {
  color: var(--text-light);
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* Inline Code */
.message code {
  background: var(--code-bg);
  padding: 2px 6px;
  border-radius: var(--border-radius-sm);
  font-family: "SF Mono", "Fira Code", "Monaco", "Consolas", monospace;
  font-size: 0.875em;
  color: var(--code-text);
  border: 1px solid var(--border-color);
}

.message.user code {
  background: var(--code-bg-dark);
  color: var(--text-light);
  border: none;
}

/* Line breaks */
.message br {
  display: block;
  content: "";
  margin-top: var(--space-2);
}

/* Nested content spacing */
.message > div > * {
  margin-top: var(--space-2);
  margin-bottom: var(--space-2);
}

.message > div > *:first-child {
  margin-top: 0;
}
.message > div > *:last-child {
  margin-bottom: 0;
}

/* Code Blocks (pre) */
.message pre {
  background: var(--code-bg);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: var(--space-3);
  margin: var(--space-3) 0;
  overflow-x: auto;
  font-family: "SF Mono", "Fira Code", "Monaco", "Consolas", monospace;
  font-size: 0.8125em;
  line-height: 1.6;
}

.message pre code {
  background: none;
  padding: 0;
  border: none;
  font-size: inherit;
}

.message.user pre {
  background: var(--code-bg-dark);
  border: none;
}

/* Tables */
.message table {
  border-collapse: collapse;
  margin: var(--space-3) 0;
  font-size: 0.875em;
  width: 100%;
  background: var(--bg-primary);
  border-radius: var(--border-radius-md);
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.message th,
.message td {
  border: 1px solid var(--border-color);
  padding: var(--space-2) var(--space-3);
  text-align: left;
}

.message th {
  background-color: var(--bg-tertiary);
  font-weight: var(--font-weight-semibold);
  font-size: 0.8125em;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-secondary);
}

.message tr:hover td {
  background-color: var(--bg-secondary);
}

/* ============================================
   CHAT INPUT AREA
   ============================================ */
.chat-input {
  display: flex;
  padding: var(--space-3) var(--space-4);
  background-color: var(--bg-primary);
  border-top: 1px solid var(--border-color);
  position: relative;
  flex-shrink: 0;
}

.chat-input input {
  flex: 1;
  padding: var(--space-3) 52px var(--space-3) var(--space-5);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-full);
  background-color: var(--bg-input);
  color: var(--input-text);
  outline: none;
  font-size: var(--font-size-base);
  font-family: inherit;
  transition:
    border-color var(--transition-fast) var(--ease-out),
    background-color var(--transition-fast) var(--ease-out),
    box-shadow var(--transition-fast) var(--ease-out);
}

.chat-input input:focus {
  border-color: var(--primary-color);
  background-color: var(--bg-input-focus);
  box-shadow: 0 0 0 3px var(--primary-color-light);
}

.chat-input input::placeholder {
  color: var(--text-muted);
}

/* Send Button */
.chat-input button {
  position: absolute;
  right: calc(var(--space-4) + var(--space-2));
  top: 50%;
  transform: translateY(-50%);
  width: 38px;
  height: 38px;
  border-radius: var(--border-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-hover) 100%);
  box-shadow: var(--shadow-sm);
  transition:
    transform var(--transition-fast) var(--ease-spring),
    box-shadow var(--transition-fast) var(--ease-out),
    opacity var(--transition-fast) var(--ease-out);
}

.chat-input button:hover:not(:disabled) {
  transform: translateY(-50%) scale(1.08);
  box-shadow: var(--shadow-md);
}

.chat-input button:active:not(:disabled) {
  transform: translateY(-50%) scale(0.95);
}

.chat-input button:focus-visible {
  outline: 3px solid var(--primary-color-light);
  outline-offset: 2px;
}

.chat-input button svg {
  width: 18px;
  height: 18px;
  color: var(--text-light);
}

/* Disabled States */
.chat-input input.disabled,
.chat-input input:disabled {
  background-color: var(--bg-tertiary);
  color: var(--text-muted);
  cursor: not-allowed;
  opacity: 0.7;
}

.chat-input button.disabled,
.chat-input button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: translateY(-50%);
}

/* Offline State */
.chat-input input.offline {
  background-color: var(--offline-color);
  color: var(--offline-text);
  border: 1px solid var(--offline-border);
}

/* ============================================
   WIDE MODE
   ============================================ */
.chat-window.wide {
  width: 720px;
}

/* ============================================
   MOBILE RESPONSIVE STYLES
   ============================================ */
@media (max-width: 480px) {
  .chat-window,
  .chat-window.wide {
    position: fixed;
    width: 100%;
    height: 100%;
    max-height: 100vh;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0;
    border-radius: 0;
    border: none;
    animation: slideUpMobile var(--transition-slow) var(--ease-out);
    z-index: 9999;
  }

  @keyframes slideUpMobile {
    from {
      transform: translateY(100%);
      opacity: 0.8;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .ai-chat-widget-container {
    bottom: var(--space-4);
    right: var(--space-4);
  }

  .ai-chat-widget-container .chat-toggle {
    z-index: 9998;
    width: 56px;
    height: 56px;
  }

  .ai-chat-widget-container.left {
    left: var(--space-4);
    right: auto;
  }

  .chat-header {
    padding: var(--space-4);
    border-radius: 0;
  }

  .chat-messages {
    padding: var(--space-4);
  }

  .message {
    max-width: 90%;
  }

  .chat-input {
    padding: var(--space-3);
    padding-bottom: max(var(--space-3), env(safe-area-inset-bottom));
  }

  .chat-input input {
    padding: var(--space-3) 48px var(--space-3) var(--space-4);
    font-size: 16px; /* Prevent iOS zoom on focus */
  }

  .chat-input button {
    width: 36px;
    height: 36px;
    right: calc(var(--space-3) + var(--space-2));
  }
}

/* Tablet breakpoint */
@media (min-width: 481px) and (max-width: 768px) {
  .chat-window {
    width: 380px;
    height: min(600px, 75vh);
  }

  .chat-window.wide {
    width: 600px;
  }
}

/* ============================================
   TYPING INDICATOR
   ============================================ */
.typing-indicator {
  background: var(--bg-primary) !important;
  padding: var(--space-3) var(--space-4) !important;
  display: flex;
  align-items: center;
  width: fit-content;
  min-width: 64px;
  border-bottom-left-radius: var(--space-1) !important;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
}

.typing-bubble-container {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
  height: 20px;
  padding-bottom: 6px;
}

.typing-indicator span,
.typing-bubble {
  width: 8px !important;
  height: 8px !important;
  background: var(--typing-dot);
  border-radius: var(--border-radius-full);
  animation: typingBounce 1.4s infinite ease-in-out !important;
  display: block !important;
  margin: 0 2px !important;
  flex-shrink: 0;
  will-change: transform;
  transform-origin: center bottom;
}

/* .typing-indicator span:nth-child(1),
.typing-bubble:nth-child(1) {
  animation-delay: 0s !important;
}

.typing-indicator span:nth-child(2),
.typing-bubble:nth-child(2) {
  animation-delay: 0.5s !important;
}

.typing-indicator span:nth-child(3),
.typing-bubble:nth-child(3) {
  animation-delay: 1s !important;
}  */

.typing-indicator span:nth-child(1),
.typing-bubble:nth-child(1) {
  animation-delay: 0s !important;
}

.typing-indicator span:nth-child(2),
.typing-bubble:nth-child(2) {
  animation-delay: 0.2s !important;
}

.typing-indicator span:nth-child(3),
.typing-bubble:nth-child(3) {
  animation-delay: 0.4s !important;
}

@keyframes typingBounce {
  0%,
  60%,
  100% {
    transform: translateY(0px);
    opacity: 0.6;
  }
  30% {
    transform: translateY(-6px);
    opacity: 1;
  }
}

/* ============================================
   WIDGET CONTAINER POSITIONING
   ============================================ */
.ai-chat-widget-container {
  position: fixed;
  bottom: var(--space-5);
  right: var(--space-5);
  z-index: var(--z-widget);
}

.ai-chat-widget-container.left {
  left: var(--space-5);
  right: auto;
}

.ai-chat-widget-container.left .chat-window {
  transform-origin: bottom left;
}

/* ============================================
   LOAD PREVIOUS CONVERSATION
   ============================================ */
.load-previous-container {
  display: flex;
  justify-content: center;
  margin-bottom: var(--space-4);
  padding-top: var(--space-2);
}

.load-previous-btn {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background-color: var(--bg-primary);
  color: var(--primary-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-full);
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition:
    background-color var(--transition-fast) var(--ease-out),
    box-shadow var(--transition-fast) var(--ease-out),
    transform var(--transition-fast) var(--ease-out);
  box-shadow: var(--shadow-sm);
}

.load-previous-btn:hover {
  background-color: var(--bg-secondary);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.load-previous-btn:active {
  transform: translateY(0);
}

.load-previous-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.load-previous-btn svg {
  width: 16px;
  height: 16px;
}

/* ============================================
   LOADING SPINNER
   ============================================ */
.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-color);
  border-left-color: var(--primary-color);
  border-radius: var(--border-radius-full);
  animation: spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   MESSAGE GROUPS
   ============================================ */
.message-group {
  margin-bottom: var(--space-5);
}

.message-group .message {
  margin-bottom: 3px;
}

.message-group .message:last-child {
  margin-bottom: var(--space-2);
}

/* Grouped message border radius adjustments */
.message-group.user .message:not(:last-child) {
  border-bottom-right-radius: var(--space-1);
}

.message-group.user .message:not(:first-child) {
  border-top-right-radius: var(--border-radius-md);
}

.message-group.bot .message:not(:last-child) {
  border-bottom-left-radius: var(--space-1);
}

.message-group.bot .message:not(:first-child) {
  border-top-left-radius: var(--border-radius-md);
}
/* ============================================
   MESSAGE FOOTER (Timestamp + Actions)
   ============================================ */
.message-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: var(--space-2);
  gap: var(--space-2);
}

.message-timestamp {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  opacity: 0.8;
  display: block;
  white-space: nowrap;
}

.message.user .message-footer .message-timestamp {
  text-align: right;
  color: rgba(255, 255, 255, 0.7);
}

.message.bot .message-footer .message-timestamp {
  text-align: left;
}

/* ============================================
   MESSAGE ACTIONS (Copy, etc.)
   ============================================ */
.message-actions {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  opacity: 0;
  transition: opacity var(--transition-fast) var(--ease-out);
  margin-left: auto;
}

.message.bot:hover .message-actions,
.message.bot:focus-within .message-actions {
  opacity: 1;
}

.action-button {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  cursor: pointer;
  transition:
    background-color var(--transition-fast) var(--ease-out),
    border-color var(--transition-fast) var(--ease-out),
    transform var(--transition-fast) var(--ease-spring),
    color var(--transition-fast) var(--ease-out);
  padding: 0;
}

.action-button:hover {
  background-color: var(--bg-tertiary);
  border-color: var(--border-light);
  color: var(--text-primary);
  transform: scale(1.08);
}

.action-button:active {
  transform: scale(0.95);
}

.action-button:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.action-button svg {
  width: 14px;
  height: 14px;
}

/* Copy button success state */
.copy-button.copied {
  background-color: var(--success-color);
  color: var(--text-light);
  border-color: var(--success-color);
  animation: popIn var(--transition-normal) var(--ease-spring);
}

@keyframes popIn {
  0% {
    transform: scale(0.8);
  }
  50% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
}

/* ============================================
   ATTRIBUTION FOOTER
   ============================================ */
.chat-attribution {
  text-align: center;
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  border-top: 1px solid var(--border-color);
  background: var(--bg-primary);
  flex-shrink: 0;
}

.chat-attribution a {
  color: inherit;
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  transition: color var(--transition-fast) var(--ease-out);
}

.chat-attribution a:hover {
  color: var(--primary-color);
}

/* Hide empty action containers */
.message-actions:empty {
  display: none;
}

/* ============================================
   ACCESSIBILITY & FOCUS STATES
   ============================================ */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .message.bot {
    border-width: 2px;
  }

  .chat-input input {
    border-width: 2px;
  }

  .action-button {
    border-width: 2px;
  }
}

/* ============================================
   PRINT STYLES
   ============================================ */
@media print {
  .ai-chat-widget-container {
    display: none !important;
  }
}

/* ============================================
   SCROLL DOWN BUTTON POSITIONING
   ============================================ */
.scroll-down-btn {
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-dropdown);
}
